/**
 * speechToISOService.js — Speech-to-ISO 14224 Bridge
 * 
 * Analyzes transcribed speech from monteurs and maps it to
 * structured ISO 14224 failure data using keyword matching.
 * 
 * Includes a learning mechanism: unmatched words can be mapped
 * by the monteur and saved for future recognition.
 * 
 * Privacy: Runs 100% locally. No external API calls.
 */

/**
 * Normalize text for matching: lowercase, strip punctuation, trim
 * @param {string} text
 * @returns {string}
 */
function normalize(text) {
    return (text || '')
        .toLowerCase()
        .replace(/[.,;:!?'"()\-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Tokenize text into individual words
 * @param {string} text - normalized text
 * @returns {string[]}
 */
function tokenize(text) {
    return normalize(text).split(' ').filter(w => w.length > 1);
}

/**
 * Find matches for a given ISO category against vocabulary
 * Supports multi-word keywords (e.g. "draait niet" → "Seized")
 * Returns the longest (most specific) match
 * 
 * @param {string} normalizedText - the full normalized transcription
 * @param {Array<{keyword: string, value: string, verified?: boolean}>} vocabList - vocabulary entries for one category
 * @returns {{ match: string|null, keyword: string|null, matchedKeywords: string[] }}
 */
function findCategoryMatch(normalizedText, vocabList) {
    if (!vocabList || vocabList.length === 0) {
        return { match: null, keyword: null, matchedKeywords: [] };
    }

    let bestMatch = null;
    let bestKeyword = null;
    let bestLength = 0;
    const matchedKeywords = [];

    for (const entry of vocabList) {
        const entryKeyword = normalize(entry.keyword);
        if (!entryKeyword) continue;

        if (normalizedText.includes(entryKeyword)) {
            matchedKeywords.push(entryKeyword);
            // Prefer longer (more specific) keywords
            if (entryKeyword.length > bestLength) {
                bestMatch = entry.value;
                bestKeyword = entryKeyword;
                bestLength = entryKeyword.length;
            }
        }
    }

    return { match: bestMatch, keyword: bestKeyword, matchedKeywords };
}

/**
 * Find words in the transcription that were not matched by any vocabulary entry
 * Filters out common Dutch stop words to avoid noise
 * 
 * @param {string[]} tokens - tokenized transcription
 * @param {string[]} allMatchedKeywords - all keywords that were matched
 * @returns {string[]} - unmatched content words
 */
function findUnmatchedWords(tokens, allMatchedKeywords) {
    const stopWords = new Set([
        'de', 'het', 'een', 'en', 'van', 'in', 'is', 'op', 'te', 'dat',
        'die', 'voor', 'zijn', 'met', 'niet', 'als', 'aan', 'er', 'maar',
        'om', 'ook', 'nog', 'bij', 'na', 'dan', 'wel', 'al', 'was', 'wat',
        'zo', 'uit', 'ze', 'we', 'je', 'ik', 'dit', 'hij', 'kan', 'wordt', 'door',
        'heel', 'erg', 'beetje', 'best', 'veel', 'meer', 'heeft', 'moet',
        'naar', 'waar', 'hier', 'daar', 'toen', 'dus', 'want', 'heb', 'ben'
    ]);

    // Build set of all characters covered by matched keywords
    const matchedWordSet = new Set();
    for (const kw of allMatchedKeywords) {
        for (const part of kw.split(' ')) {
            matchedWordSet.add(part);
        }
    }

    return tokens.filter(word =>
        !stopWords.has(word) &&
        !matchedWordSet.has(word) &&
        word.length > 2
    );
}

/**
 * Main analysis function: transcription text → ISO 14224 structured data
 * 
 * @param {string} text - raw transcription from Whisper/Web Speech
 * @param {object} vocabulary - { failures: [...], causes: [...], components: [...] }
 * @returns {{
 *   component: string|null,
 *   failureMode: string|null,
 *   cause: string|null,
 *   confidence: number,
 *   unmatchedWords: string[],
 *   matchDetails: { component: string|null, failureMode: string|null, cause: string|null }
 * }}
 */
export function analyzeTranscription(text, vocabulary) {
    if (!text || !vocabulary) {
        return {
            component: null,
            failureMode: null,
            cause: null,
            confidence: 0,
            unmatchedWords: [],
            matchDetails: { component: null, failureMode: null, cause: null }
        };
    }

    const normalizedText = normalize(text);
    const tokens = tokenize(text);

    // Match against each ISO category
    const componentResult = findCategoryMatch(normalizedText, vocabulary.components);
    const failureResult = findCategoryMatch(normalizedText, vocabulary.failures);
    const causeResult = findCategoryMatch(normalizedText, vocabulary.causes);

    // Collect all matched keywords for unmatched detection
    const allMatchedKeywords = [
        ...componentResult.matchedKeywords,
        ...failureResult.matchedKeywords,
        ...causeResult.matchedKeywords
    ];

    // Find unmatched content words
    const unmatchedWords = findUnmatchedWords(tokens, allMatchedKeywords);

    // Calculate confidence (0-1): ratio of fields filled
    const filledCount = [componentResult.match, failureResult.match, causeResult.match]
        .filter(Boolean).length;
    const confidence = filledCount / 3;

    return {
        component: componentResult.match,
        failureMode: failureResult.match,
        cause: causeResult.match,
        confidence,
        unmatchedWords,
        matchDetails: {
            component: componentResult.keyword,
            failureMode: failureResult.keyword,
            cause: causeResult.keyword
        }
    };
}

/**
 * Get ISO value suggestions for a specific category
 * Used by the learner to show options when a word is unmatched
 * 
 * @param {string} category - 'failures' | 'causes' | 'components'
 * @param {object} vocabulary - full vocabulary
 * @returns {string[]} - unique ISO values in that category
 */
export function getISOSuggestions(category, vocabulary) {
    const list = vocabulary?.[category] || [];
    const values = new Set(list.map(item => item.value));
    return [...values].sort();
}

/**
 * Add a learned word to the vocabulary
 * Returns a new vocabulary object (immutable)
 * 
 * @param {object} vocabulary - current vocabulary
 * @param {string} category - 'failures' | 'causes' | 'components'
 * @param {string} keyword - the new word/phrase to learn
 * @param {string} value - ISO 14224 value to map to
 * @returns {object} - new vocabulary with the learned word added
 */
export function addLearnedWord(vocabulary, category, keyword, value) {
    if (!vocabulary || !category || !keyword || !value) return vocabulary;

    const existing = vocabulary[category] || [];

    // Check if keyword already exists
    const normalizedKeyword = normalize(keyword);
    const alreadyExists = existing.some(
        item => normalize(item.keyword) === normalizedKeyword
    );
    if (alreadyExists) return vocabulary;

    return {
        ...vocabulary,
        [category]: [
            ...existing,
            {
                keyword: normalizedKeyword,
                value,
                verified: false,
                learnedAt: new Date().toISOString()
            }
        ]
    };
}

/**
 * Count unverified words in the vocabulary
 * @param {object} vocabulary
 * @returns {{ failures: number, causes: number, components: number, total: number }}
 */
export function countUnverified(vocabulary) {
    if (!vocabulary) return { failures: 0, causes: 0, components: 0, total: 0 };

    const count = (list) => (list || []).filter(item => item.verified === false).length;
    const failures = count(vocabulary.failures);
    const causes = count(vocabulary.causes);
    const components = count(vocabulary.components);

    return { failures, causes, components, total: failures + causes + components };
}

export default {
    analyzeTranscription,
    getISOSuggestions,
    addLearnedWord,
    countUnverified
};
